import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Send, 
  Paperclip, 
  Smile, 
  MoreVertical,
  Check,
  CheckCheck,
  Clock,
  Phone,
  Video,
  AlertCircle,
  Sparkles,
  MessageSquare,
  AlertTriangle,
  Info
} from 'lucide-react';
import axios from 'axios';

const API_URL = window.location.origin;

const WhatsAppMessenger = ({ open, onClose, lead, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [coldLeadStatus, setColdLeadStatus] = useState(null);
  const [loadingColdStatus, setLoadingColdStatus] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [usingTemplate, setUsingTemplate] = useState(false); // Track if template is being used
  const [timeRemaining, setTimeRemaining] = useState(null); // 24-hour countdown
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Define silentCheckNewMessages BEFORE useEffect that depends on it
  const silentCheckNewMessages = useCallback(async () => {
    // Silent background check - no loading state, no UI disruption
    if (!lead?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/crm/whatsapp/history/${lead.id}?limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const latestMessages = response.data.messages || [];
      
      if (latestMessages.length > 0) {
        const latestId = latestMessages[0].id;
        
        // Only update if there are NEW messages
        if (lastMessageId && latestId !== lastMessageId) {
          console.log('📩 New message detected, updating silently...');
          
          // Find messages that are newer than our last known message
          const newMessages = [];
          for (const msg of latestMessages) {
            if (msg.id === lastMessageId) break;
            newMessages.push(msg);
          }
          
          if (newMessages.length > 0) {
            // Prepend new messages (they come in reverse chronological order)
            setMessages(prev => [...newMessages, ...prev]);
            setLastMessageId(latestId);
            
            // Scroll to new message if it's an inbound
            if (newMessages.some(m => m.type === 'inbound')) {
              setTimeout(() => scrollToBottom(), 100);
            }
          }
        }
      }
    } catch (error) {
      // Silent failure - don't disrupt user experience
      console.error('Silent message check failed:', error);
    }
  }, [lead?.id, lastMessageId]);

  useEffect(() => {
    if (open && lead) {
      // Initial load
      loadMessages();
      loadTemplates();
      loadAISuggestions();
      checkColdLeadStatus();
      
      // Poll for new messages every 5 seconds using silent check
      const intervalId = setInterval(() => {
        silentCheckNewMessages();
      }, 5000);
      
      pollingIntervalRef.current = intervalId;
      
      return () => {
        // Cleanup polling on unmount or when dialog closes
        clearInterval(intervalId);
        pollingIntervalRef.current = null;
      };
    }
  }, [open, lead?.id, silentCheckNewMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 24-hour countdown timer
  useEffect(() => {
    if (!coldLeadStatus || coldLeadStatus.is_cold) {
      setTimeRemaining(null);
      return;
    }

    // Calculate time remaining
    const updateCountdown = () => {
      if (!coldLeadStatus.last_inbound_at) {
        setTimeRemaining(null);
        return;
      }

      const lastInbound = new Date(coldLeadStatus.last_inbound_at);
      const now = new Date();
      const elapsed = now - lastInbound;
      const twentyFourHours = 24 * 60 * 60 * 1000;
      const remaining = twentyFourHours - elapsed;

      if (remaining <= 0) {
        setTimeRemaining(null);
        return;
      }

      // Format as HH:MM:SS
      const hours = Math.floor(remaining / (60 * 60 * 1000));
      const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
      const seconds = Math.floor((remaining % (60 * 1000)) / 1000);
      setTimeRemaining({ hours, minutes, seconds, total: remaining });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [coldLeadStatus]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/crm/whatsapp/history/${lead.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newMessages = response.data.messages || [];
      setMessages(newMessages);
      
      // Track last message ID for silent polling
      if (newMessages.length > 0) {
        setLastMessageId(newMessages[0].id);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/crm/whatsapp/templates/custom`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('✅ Templates loaded:', response.data.templates?.length || 0);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('❌ Failed to load templates:', error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadAISuggestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/crm/whatsapp/ai-suggestions/${lead.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiSuggestions(response.data.ai_suggestions);
    } catch (error) {
      console.error('Failed to load AI suggestions:', error);
    }
  };

  const checkColdLeadStatus = async () => {
    setLoadingColdStatus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/crm/whatsapp/check-cold-lead/${lead.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setColdLeadStatus(response.data);
      console.log('🔍 Cold lead status:', response.data);
      
      // If cold lead, auto-show templates
      if (response.data.is_cold && response.data.requires_template) {
        setShowTemplates(true);
      }
    } catch (error) {
      console.error('Failed to check cold lead status:', error);
      // Default to warm if check fails
      setColdLeadStatus({ is_cold: false, requires_template: false });
    } finally {
      setLoadingColdStatus(false);
    }
  };

  const fillTemplate = (templateText) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    return templateText
      .replace(/{name}/g, lead.name || 'there')
      .replace(/{company}/g, 'Vibe6 Digital LLP')
      .replace(/{agent_name}/g, user.name || 'Sales Team')
      .replace(/{email}/g, lead.email || '')
      .replace(/{requirement}/g, lead.remarks || 'your requirements');
  };

  const handleTemplateSelect = (template) => {
    const filledMessage = fillTemplate(template.template_text);
    setMessage(filledMessage);
    setUsingTemplate(true); // Mark that we're using a template
    setShowTemplates(false);
    textareaRef.current?.focus();
  };

  const handleMessageChange = (e) => {
    const newValue = e.target.value;
    
    // If cold lead, prevent free-text typing (unless using template)
    if (coldLeadStatus?.is_cold && coldLeadStatus?.requires_template && !usingTemplate) {
      // Show warning
      if (newValue.length > message.length) {
        alert('⚠️ This is a cold lead!\n\nWhatsApp Business Policy requires using an approved template.\n\nPlease select a template from the list.');
        return;
      }
    }
    
    setMessage(newValue);
    
    // If user modifies template message, reset the template flag
    if (usingTemplate && newValue !== message) {
      setUsingTemplate(false);
    }
    
    // Show typing indicator
    if (!isTyping && newValue.length > 0) {
      setIsTyping(true);
    } else if (isTyping && newValue.length === 0) {
      setIsTyping(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || sending) return;

    // Check if cold lead trying to send free-text (but allow if template is being used)
    if (coldLeadStatus?.is_cold && coldLeadStatus?.requires_template && !usingTemplate) {
      alert('⚠️ Cannot send free-text to cold lead!\n\nWhatsApp Business Policy:\nThis customer hasn\'t messaged in 24 hours.\n\nYou must use an approved template.\n\nPlease click the 💬 button to select a template.');
      setShowTemplates(true);
      return;
    }

    const messageText = message.trim();
    const tempId = `temp-${Date.now()}`;
    
    // Clear input and template flag immediately
    setMessage('');
    setUsingTemplate(false);
    
    // Add optimistic message immediately
    const optimisticMessage = {
      id: tempId,
      type: 'outbound',
      message: messageText,
      status: 'sending',
      sent_at: new Date().toISOString(),
      from_number: null,
      to_number: lead?.phone
    };
    
    setMessages(prev => [optimisticMessage, ...prev]);
    
    // Scroll to bottom
    setTimeout(() => scrollToBottom(), 100);

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/crm/whatsapp/send`,
        {
          lead_id: lead.id,
          message: messageText
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('✅ WhatsApp sent successfully:', response.data);

      // Update optimistic message with actual data
      setMessages(prev => prev.map(msg => 
        msg.id === tempId 
          ? { ...msg, status: 'sent', id: response.data.id || tempId }
          : msg
      ));

      // Silent polling will handle status updates automatically
      if (onSuccess) onSuccess();
      
    } catch (error) {
      console.error('❌ Failed to send WhatsApp:', error.response?.data || error);
      
      // Update optimistic message to show error
      setMessages(prev => prev.map(msg => 
        msg.id === tempId 
          ? { ...msg, status: 'failed', error: 'Failed to send' }
          : msg
      ));
      
      const errorMsg = error.response?.data?.detail || 'Failed to send WhatsApp message';
      
      // Check if it's a cold lead error
      if (typeof errorMsg === 'object' && errorMsg.error === 'template_required') {
        alert(`⚠️ Template Required!\n\n${errorMsg.message}\n\nPlease select an approved template.`);
        setShowTemplates(true);
      } else if (errorMsg.includes('not configured')) {
        alert('⚠️ Twilio WhatsApp is not configured.\n\nPlease ask your Super Admin to configure Twilio credentials.');
      } else {
        alert(`❌ ${errorMsg}`);
      }
    } finally {
      setSending(false);
      // Focus back on textarea
      textareaRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const getMessageStatus = (msg) => {
    if (msg.status === 'sent') {
      return <CheckCheck className="h-3 w-3 text-blue-500" />;
    } else if (msg.status === 'delivered') {
      return <CheckCheck className="h-3 w-3 text-blue-500" />;
    } else if (msg.status === 'read') {
      return <CheckCheck className="h-3 w-3 text-blue-600" />;
    } else if (msg.status === 'delivery_blocked') {
      return (
        <div className="flex items-center gap-1" title={msg.reason || 'Blocked by Meta'}>
          <AlertTriangle className="h-3 w-3 text-orange-500" />
          <span className="text-[9px] text-orange-600 font-medium">BLOCKED</span>
        </div>
      );
    } else if (msg.status === 'failed') {
      return <AlertCircle className="h-3 w-3 text-red-500" />;
    } else {
      return <Clock className="h-3 w-3 text-gray-400" />;
    }
  };

  const groupMessagesByDate = (messages) => {
    const grouped = {};
    messages.forEach(msg => {
      const date = formatDate(msg.sent_at);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(msg);
    });
    return grouped;
  };

  const groupedMessages = groupMessagesByDate([...messages].reverse());

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[85vh] p-0 gap-0">
        {/* Header - WhatsApp Style */}
        <DialogHeader className="bg-[#008069] text-white p-4 flex-row items-center space-y-0">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-semibold">
              {lead?.name?.charAt(0) || 'L'}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-white text-base font-normal">
                {lead?.name || 'Lead'}
              </DialogTitle>
              <p className="text-xs text-white/80">{lead?.phone || 'No phone'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => setShowAISuggestions(!showAISuggestions)}
            >
              <Sparkles className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
            >
              <Video className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
            >
              <Phone className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
            >
              <MoreVertical className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        {/* Cold Lead Warning Banner - Compact Ribbon */}
        {!loadingColdStatus && coldLeadStatus?.is_cold && coldLeadStatus?.requires_template && (
          <div className="mx-4 mt-2 px-3 py-1.5 bg-orange-50 border-l-4 border-orange-500 dark:bg-orange-950 rounded flex items-center self-start">
            <AlertTriangle className="h-3.5 w-3.5 text-orange-600 flex-shrink-0" />
            <div className="flex-1 min-w-0 ml-2">
              <span className="text-xs font-medium text-orange-900 dark:text-orange-100">
                ⚠️ Cold Lead - Template Required
              </span>
              <span className="text-xs text-orange-700 dark:text-orange-300 ml-1.5">
                · {coldLeadStatus.reason}
              </span>
            </div>
          </div>
        )}

        {/* Warm Lead Info - Compact Ribbon with Countdown */}
        {!loadingColdStatus && coldLeadStatus && !coldLeadStatus.is_cold && (
          <div className="mx-4 mt-2 px-3 py-1.5 bg-green-50 border-l-4 border-green-500 dark:bg-green-950 rounded flex items-center justify-between">
            <div className="flex items-center">
              <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
              <div className="flex-1 min-w-0 ml-2">
                <span className="text-xs font-medium text-green-900 dark:text-green-100">
                  ✅ Warm Lead - Free-text Allowed
                </span>
                <span className="text-xs text-green-700 dark:text-green-300 ml-1.5">
                  · Customer messaged {coldLeadStatus.hours_since_last_message}h ago
                </span>
              </div>
            </div>
            {timeRemaining && (
              <div className="flex items-center gap-1.5 ml-3 px-2 py-0.5 bg-green-100 dark:bg-green-900 rounded">
                <Clock className="h-3 w-3 text-green-700 dark:text-green-300" />
                <span className="text-xs font-mono font-medium text-green-900 dark:text-green-100">
                  {String(timeRemaining.hours).padStart(2, '0')}:
                  {String(timeRemaining.minutes).padStart(2, '0')}:
                  {String(timeRemaining.seconds).padStart(2, '0')}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Messages Area - WhatsApp Style */}
        <div 
          className="flex-1 overflow-hidden bg-[#efeae2] relative"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d9d9d9' fill-opacity='0.1'%3E%3Cpath d='M0 0h100v100H0z'/%3E%3C/g%3E%3C/svg%3E")`
          }}
        >
          <ScrollArea className="h-full p-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading messages...</div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <MessageSquare className="h-16 w-16 mb-4 opacity-20" />
                <p className="text-lg font-medium">No messages yet</p>
                <p className="text-sm">Start the conversation with {lead?.name}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedMessages).map(([date, dateMessages]) => (
                  <div key={date}>
                    {/* Date Divider */}
                    <div className="flex justify-center my-4">
                      <div className="bg-white/90 shadow-sm px-3 py-1 rounded-lg text-xs text-gray-600">
                        {date}
                      </div>
                    </div>

                    {/* Messages for this date */}
                    {dateMessages.map((msg) => {
                      const isOutbound = msg.type === 'outbound';
                      const isInbound = msg.type === 'inbound';

                      return (
                        <div
                          key={msg.id}
                          className={`flex mb-2 ${isOutbound ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%] ${isOutbound ? 'order-2' : 'order-1'}`}>
                            {/* Sender Name */}
                            <div className={`text-xs mb-1 px-2 ${isOutbound ? 'text-right text-gray-600' : 'text-left text-gray-700'}`}>
                              {isOutbound ? 'Vibe6 (me)' : (lead?.name || 'Customer')}
                            </div>

                            {/* Message Bubble */}
                            <div
                              className={`rounded-lg p-3 shadow-sm ${
                                isOutbound
                                  ? 'bg-[#d9fdd3] rounded-tr-none'
                                  : 'bg-white rounded-tl-none'
                              }`}
                            >
                              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                                {msg.message}
                              </p>

                              {/* Media */}
                              {msg.has_media && msg.media_url && (
                                <div className="mt-2">
                                  <a
                                    href={msg.media_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                  >
                                    <Paperclip className="h-3 w-3" />
                                    View attachment
                                  </a>
                                </div>
                              )}

                              {/* Time and Status */}
                              <div className="flex items-center justify-end gap-1 mt-1">
                                <span className="text-[10px] text-gray-500">
                                  {formatTime(msg.sent_at)}
                                </span>
                                {isOutbound && getMessageStatus(msg)}
                              </div>

                              {/* Error */}
                              {msg.error && (
                                <div className="mt-1 text-xs text-red-600 flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  Failed to send
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* AI Suggestions Overlay */}
          {showAISuggestions && aiSuggestions && (
            <div className="absolute top-0 left-0 right-0 bg-white/95 backdrop-blur-sm p-4 shadow-lg border-b max-h-[50%] overflow-auto">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  AI Suggestions
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAISuggestions(false)}
                >
                  ✕
                </Button>
              </div>
              <div className="space-y-2">
                {aiSuggestions.recommended_templates?.slice(0, 3).map((template, idx) => (
                  <Card
                    key={idx}
                    className="cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => {
                      setMessage(fillTemplate(template.template_text));
                      setShowAISuggestions(false);
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{template.name}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {template.template_text.substring(0, 100)}...
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {template.score}% match
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Templates Overlay */}
          {showTemplates && (
            <div className="absolute bottom-0 left-0 right-0 bg-white border-t shadow-lg max-h-[70%] overflow-auto z-10">
              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Message Templates
                      {coldLeadStatus?.is_cold && (
                        <Badge variant="destructive" className="text-xs">
                          Required
                        </Badge>
                      )}
                    </h3>
                    {coldLeadStatus?.is_cold && (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Cold lead detected - You must use an approved template
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTemplates(false)}
                    disabled={coldLeadStatus?.is_cold}
                  >
                    {coldLeadStatus?.is_cold ? '(Required)' : '✕'}
                  </Button>
                </div>

                {/* Template Categories */}
                <div className="space-y-4">
                  {loadingTemplates ? (
                    <div className="text-center py-8">
                      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
                      <p className="text-sm text-muted-foreground">Loading templates...</p>
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-20" />
                      <p className="text-sm text-muted-foreground">No templates available</p>
                      <p className="text-xs text-muted-foreground mt-1">Contact admin to create templates</p>
                    </div>
                  ) : (
                    <>
                      {/* Group templates by category */}
                      {Object.entries(
                        templates.reduce((acc, template) => {
                          const cat = template.category || 'other';
                          if (!acc[cat]) acc[cat] = [];
                          acc[cat].push(template);
                          return acc;
                        }, {})
                      ).map(([category, categoryTemplates]) => (
                        <div key={category}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs capitalize">
                              {category.replace('_', ' ')}
                            </Badge>
                            <div className="h-px flex-1 bg-border"></div>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            {categoryTemplates.map((template) => {
                              // Check if US number and MARKETING template
                              const isUSNumber = lead?.phone?.startsWith('+1');
                              const isMarketing = template.category?.toUpperCase() === 'MARKETING';
                              const blockedForUS = isUSNumber && isMarketing;
                              
                              return (
                              <Card
                                key={template.id}
                                className={`cursor-pointer transition-colors ${
                                  blockedForUS 
                                    ? 'opacity-50 cursor-not-allowed bg-red-50' 
                                    : 'hover:bg-accent hover:border-primary'
                                }`}
                                onClick={() => !blockedForUS && handleTemplateSelect(template)}
                              >
                                <CardContent className="p-3">
                                  <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium">{template.name}</p>
                                        {template.category && (
                                          <Badge 
                                            variant={isMarketing ? "default" : "secondary"} 
                                            className="text-[10px] px-1.5 py-0"
                                          >
                                            {template.category}
                                          </Badge>
                                        )}
                                      </div>
                                      {template.use_case && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                          💡 {template.use_case}
                                        </p>
                                      )}
                                      
                                      {/* Warnings */}
                                      {isMarketing && !blockedForUS && (
                                        <div className="flex items-start gap-1 mt-2 text-xs text-orange-600">
                                          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                          <span>May be throttled by Meta for cold outreach</span>
                                        </div>
                                      )}
                                      
                                      {blockedForUS && (
                                        <div className="flex items-start gap-1 mt-2 text-xs text-red-600 font-medium">
                                          <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                          <span>❌ Not deliverable to US numbers (Meta Policy)</span>
                                        </div>
                                      )}
                                    </div>
                                    {template.compliance_status === 'approved' && !blockedForUS && (
                                      <Badge variant="secondary" className="text-xs ml-2">
                                        ✓ Approved
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground line-clamp-2 bg-muted/50 p-2 rounded">
                                    {template.template_text.substring(0, 150)}...
                                  </p>
                                  <div className="flex items-center gap-2 mt-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-xs h-7"
                                      disabled={blockedForUS}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!blockedForUS) {
                                          handleTemplateSelect(template);
                                        }
                                      }}
                                    >
                                      {blockedForUS ? '❌ Blocked' : 'Use Template'}
                                    </Button>
                                    {template.variables && template.variables.length > 0 && (
                                      <span className="text-xs text-muted-foreground">
                                        Variables: {template.variables.join(', ')}
                                      </span>
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>

                {/* Policy Info Footer */}
                {coldLeadStatus?.is_cold && (
                  <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded">
                    <div className="flex gap-2">
                      <Info className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-orange-800 dark:text-orange-200">
                        <div className="font-semibold mb-1">WhatsApp Business Policy</div>
                        <div>
                          • Free-text messages only allowed within 24 hours of customer&apos;s last message<br />
                          • Cold leads (never messaged or &gt;24h) require approved templates<br />
                          • Violations can result in account suspension
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Input Area - WhatsApp Style */}
        <div className="bg-[#f0f2f5] p-3 border-t">
          {/* Typing indicator */}
          {isTyping && (
            <div className="text-xs text-gray-500 mb-1 px-2">
              typing...
            </div>
          )}
          
          <div className="flex items-end gap-2">
            {/* Template Button */}
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-600 hover:bg-gray-200 mb-1"
              onClick={() => setShowTemplates(!showTemplates)}
              disabled={sending}
            >
              <MessageSquare className="h-5 w-5" />
            </Button>

            {/* Emoji Button */}
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-600 hover:bg-gray-200 mb-1"
              disabled={sending}
            >
              <Smile className="h-5 w-5" />
            </Button>

            {/* Message Input */}
            <div className="flex-1 bg-white rounded-lg relative">
              <Textarea
                ref={textareaRef}
                id="whatsapp-message-input"
                name="whatsapp-message"
                value={message}
                onChange={handleMessageChange}
                onKeyPress={handleKeyPress}
                placeholder={
                  coldLeadStatus?.is_cold 
                    ? "⚠️ Template required - Click 💬 to select" 
                    : isTyping 
                    ? "typing..." 
                    : "Type a message"
                }
                className={`min-h-[44px] max-h-[120px] border-0 focus-visible:ring-0 resize-none ${
                  coldLeadStatus?.is_cold ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={sending || coldLeadStatus?.is_cold}
                readOnly={coldLeadStatus?.is_cold}
              />
              {coldLeadStatus?.is_cold && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-3 py-1 rounded text-xs font-medium">
                    Template Required - Click 💬
                  </div>
                </div>
              )}
            </div>

            {/* Send Button */}
            <Button
              size="icon"
              className="bg-[#008069] hover:bg-[#017561] text-white rounded-full h-11 w-11 mb-1"
              onClick={handleSend}
              disabled={!message.trim() || sending}
            >
              {sending ? (
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppMessenger;
